/**
 * 统一轮询管理器
 * 在同一个定时器中执行多个轮询任务，减少定时器数量
 */

import type { AppConfig } from '@/types/config'
import { gameHeartbeat } from '@/services/api/game'

/** 轮询间隔（毫秒），默认 10 秒 */
const POLL_INTERVAL = 10000

/** 本地存储的版本号 key */
const VERSION_STORAGE_KEY = 'app_website_version'

/** 版本更新回调函数类型 */
type VersionUpdateCallback = (newVersion: string) => void | Promise<void>

/** 轮询管理器状态 */
interface PollManagerState {
  timerId: number | null
  versionUpdateCallback: VersionUpdateCallback | null
  gameSessionId: string | null
}

const state: PollManagerState = {
  timerId: null,
  versionUpdateCallback: null,
  gameSessionId: null,
}

/**
 * 从服务器获取配置
 */
async function fetchConfig(): Promise<AppConfig> {
  const res = await fetch('/config.json?ts=' + Date.now())
  return await res.json()
}

/**
 * 获取本地存储的版本号
 */
function getLocalVersion(): string | null {
  return localStorage.getItem(VERSION_STORAGE_KEY)
}

/**
 * 保存版本号到本地存储
 */
function saveLocalVersion(version: string): void {
  localStorage.setItem(VERSION_STORAGE_KEY, version)
}

/**
 * 检查版本是否有更新
 */
function checkVersionUpdate(remoteVersion: string): boolean {
  const localVersion = getLocalVersion()

  // 第一次访问，保存版本号，不提示更新
  if (!localVersion) {
    saveLocalVersion(remoteVersion)
    return false
  }

  return localVersion !== remoteVersion
}

/**
 * 设置版本更新回调函数
 */
export function setVersionUpdateCallback(callback: VersionUpdateCallback): void {
  state.versionUpdateCallback = callback
}

/**
 * 处理版本更新 - 刷新页面
 */
export function handleVersionRefresh(newVersion: string): void {
  saveLocalVersion(newVersion)
  window.location.reload()
}

/**
 * 设置游戏会话 ID（开始游戏时调用）
 */
export function setGameSession(sessionId: string | null): void {
  state.gameSessionId = sessionId
  console.log('[PollManager] 游戏会话:', sessionId ? '已设置' : '已清除')
}

/**
 * 执行轮询任务
 */
async function executePollTasks(): Promise<void> {
  // 任务 1: 配置轮询 & 版本检测
  try {
    const config = await fetchConfig()
    window.APP_CONFIG = config

    if (config.websiteVersion) {
      const hasUpdate = checkVersionUpdate(config.websiteVersion)
      if (hasUpdate && state.versionUpdateCallback) {
        await state.versionUpdateCallback(config.websiteVersion)
      }
    }
  } catch (error) {
    console.error('[PollManager] 配置更新失败:', error)
  }

  // 任务 2: 游戏心跳（如果有活跃会话）
  if (state.gameSessionId) {
    try {
      const result = await gameHeartbeat(state.gameSessionId)
      if (!result.success) {
        console.warn('[PollManager] 游戏心跳失败:', result.error)
        // 心跳失败可能意味着会话已过期，清除 sessionId
        if (result.error?.includes('会话') || result.error?.includes('session')) {
          state.gameSessionId = null
        }
      }
    } catch (error) {
      console.error('[PollManager] 游戏心跳异常:', error)
    }
  }
}

/**
 * 加载配置（首次）
 */
export async function loadConfig(checkVersion = true): Promise<AppConfig> {
  const config = await fetchConfig()
  window.APP_CONFIG = config

  if (checkVersion && config.websiteVersion) {
    const hasUpdate = checkVersionUpdate(config.websiteVersion)
    if (hasUpdate && state.versionUpdateCallback) {
      await state.versionUpdateCallback(config.websiteVersion)
    }
  }

  return config
}

/**
 * 启动轮询
 */
export function startPolling(interval = POLL_INTERVAL): void {
  if (state.timerId) {
    console.warn('[PollManager] 轮询已在运行')
    return
  }

  state.timerId = window.setInterval(executePollTasks, interval)
  console.log('[PollManager] 轮询已启动，间隔:', interval, 'ms')
}

/**
 * 停止轮询
 */
export function stopPolling(): void {
  if (state.timerId) {
    clearInterval(state.timerId)
    state.timerId = null
    console.log('[PollManager] 轮询已停止')
  }
}

/**
 * 获取轮询状态
 */
export function getPollStatus(): { running: boolean; hasGameSession: boolean } {
  return {
    running: state.timerId !== null,
    hasGameSession: state.gameSessionId !== null,
  }
}
